/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { mapBy, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import NzMoeEthnicity from '@linc-technologies/linced-common/enums/nz-moe-ethnicity';
import moment from 'moment';
import {
  compact,
  filterBy,
  findBy,
  rejectBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';

export default class IndexController extends Controller {
  @service
  session;

  @service
  fieldOptions;

  @service
  schoolSchema;

  GroupType = GroupType;
  NzMoeEthnicity = NzMoeEthnicity;
  Jurisdiction = Jurisdiction;

  @alias('session.school')
  school;

  @tracked today = moment().format('Do MMMM, YYYY');

  @computed('today', 'model.group.name')
  get basicCountsTitle() {
    return `Basic Class Counts as at ${this.today} (${this.model.group.name})`;
  }

  @computed('today', 'model.group.name')
  get primaryEthnicityTitle() {
    return `Primary Ethnicity Counts as at ${this.today} (${this.model.group.name})`;
  }

  @computed('today', 'model.group.name')
  get moeEthnicityTitle() {
    return `MoE Ethnicity Group Counts as at ${this.today} (${this.model.group.name})`;
  }

  @computed('today', 'model.group.name')
  get yearLevelsTitle() {
    return `Current Year Level Counts as at ${this.today} (${this.model.group.name})`;
  }

  @computed('today', 'model.group.name')
  get moeYearLevelsTitle() {
    return `MoE Funding Year Level Counts as at ${this.today} (${this.model.group.name})`;
  }

  @computed('model.genderField.options.[]')
  get genderOptions() {
    let field = this.get('model.genderField');
    if (isEmpty(field) || isEmpty(field.options)) {
      return [
        {
          value: 'M',
          label: 'Male',
        },
        {
          value: 'F',
          label: 'Female',
        },
      ];
    }

    return field.options;
  }

  @mapBy('genderOptions', 'value')
  genderValues;

  @computed('genderOptions.[]', 'school.jurisdiction')
  get genderNShortLabel() {
    if (this.get('school.jurisdiction') === Jurisdiction.AU_NSW) {
      // Use NSW DoE terminology (abbreviated).
      return 'NB; NS';
    }
    let option = this.genderOptions.find((item) => item.value === 'N');
    if (!isEmpty(option)) {
      // Use the label from the field option.
      return option.label;
    }
    return 'N';
  }

  @computed('model.learners.[]', 'genderOptions.[]', 'genderValues.[]')
  get genderData() {
    let { genderOptions, genderValues } = this;
    let initialCounts = genderValues.reduce(
      (counts, val) => {
        counts[val] = 0;
        return counts;
      },
      { U: 0 },
    );
    let genders = this.model.learners.reduce((data, member) => {
      let gender = member.get('gender');
      if (genderValues.includes(gender)) {
        data[gender]++;
      } else {
        data['U']++;
      }
      return data;
    }, initialCounts);

    let data = genderOptions.map((option) => {
      return {
        value: option.value,
        label: option.label,
        count: genders[option.value],
        tooltip: `${option.label}: ${genders[option.value]}`,
      };
    });
    if (genders.U) {
      data.push({
        value: '?',
        label: 'Other / Unknown',
        count: genders.U,
        tooltip: 'Other / Unknown: ' + genders.U,
      });
    }
    return data;
  }

  @computed('model.learners.[]')
  get allMOEEthnicities() {
    return compact(
      uniq(this.model.learners.map((item) => item.displayEthnicity)),
    );
  }

  @computed('model.learners.[]')
  get allPrimaryEthnicities() {
    return compact(
      uniq(this.model.learners.map((item) => item.primaryEthnicity)),
    );
  }

  @computed('model.learners.[]', 'yearLevels.[]', 'genderValues.[]')
  get breakdown() {
    let currentYear = moment().year();
    let genderCounts = this.genderValues.reduce(
      (counts, gender) => {
        counts[gender] = 0;
        return counts;
      },
      { U: 0, total: 0 },
    );
    return this.model.learners.reduce((data, member) => {
      let homeroom = member.get('homeroom');
      if (isEmpty(homeroom) || homeroom.year !== currentYear) {
        // No homeroom (loaded), or homeroom is not from this year.
        homeroom = {
          id: 'none',
          label: '[None]',
        };
      }
      let homeroomId = homeroom.id;
      let homeroomData = data.find((item) => item.homeroomId === homeroomId);
      if (isEmpty(homeroomData)) {
        data.push(
          Object.assign(
            {
              homeroomId,
              homeroom,
              ethnicities: [],
              moeEthnicities: [],
              yearLevels: Object.assign({ yearLevels: [] }, genderCounts),
              moeYearLevels: Object.assign({ yearLevels: [] }, genderCounts),
            },
            genderCounts,
          ),
        );
        homeroomData = data.find((item) => item.homeroomId === homeroomId);
      }

      // Count by gender
      let gender = member.get('gender');
      if (!this.genderValues.includes(gender)) {
        gender = 'U';
      }
      homeroomData[gender]++;

      // Count by Current Year Level
      let yearLevel = (member.get('currentYearLevel') ?? 0).toString();
      if (this.yearLevels.includes(yearLevel)) {
        let yearLevelData = homeroomData['yearLevels'].yearLevels.find(
          (item) => item.year === yearLevel,
        );
        if (isEmpty(yearLevelData)) {
          let temp = Object.assign({ year: yearLevel }, genderCounts);
          temp[gender] = 1;
          temp['total'] = 1;
          homeroomData['yearLevels'].yearLevels.push(temp);
        } else {
          yearLevelData[gender]++;
          yearLevelData.total++;
        }
        // Totals for all years
        homeroomData['yearLevels'][gender]++;
        homeroomData['yearLevels'].total++;
      }

      // Count by MOE (Funding) Year Level
      let fundingYearLevel = (member.get('fundingYearLevel') ?? 0).toString();
      if (this.yearLevels.includes(fundingYearLevel)) {
        let fundingYearLevelData = homeroomData[
          'moeYearLevels'
        ].yearLevels.find((item) => item.year === fundingYearLevel);
        if (isEmpty(fundingYearLevelData)) {
          let temp = Object.assign({ year: fundingYearLevel }, genderCounts);
          temp[gender] = 1;
          temp['total'] = 1;
          homeroomData['moeYearLevels'].yearLevels.push(temp);
        } else {
          fundingYearLevelData[gender]++;
          fundingYearLevelData.total++;
        }
        // Totals for all years
        homeroomData['moeYearLevels'][gender]++;
        homeroomData['moeYearLevels'].total++;
      }

      // Count by primary ethnicity
      let ethnicity = member.get('primaryEthnicity');
      if (!isEmpty(ethnicity)) {
        let ethnicityData = findBy(
          homeroomData['ethnicities'],
          'code',
          ethnicity,
        );
        if (isEmpty(ethnicityData)) {
          homeroomData['ethnicities'].push({
            code: ethnicity,
            count: 1,
          });
        } else {
          ethnicityData.count++;
        }
      }

      // Count by MOE ranked ethnicity
      let moeEthnicity = member.get('displayEthnicity');
      if (!isEmpty(moeEthnicity)) {
        let ethnicityData = findBy(
          homeroomData['moeEthnicities'],
          'code',
          moeEthnicity,
        );
        if (isEmpty(ethnicityData)) {
          homeroomData['moeEthnicities'].push({
            code: moeEthnicity,
            count: 1,
          });
        } else {
          ethnicityData.count++;
        }
      }

      homeroomData.total++;

      return data;
    }, []);
  }

  @computed('breakdown.@each.N')
  get hasGenderN() {
    return !!findBy(this.breakdown, 'N');
  }

  @computed('breakdown.@each.U')
  get hasGenderU() {
    return !!findBy(this.breakdown, 'U');
  }

  @computed('breakdown.[]')
  get breakdownTotals() {
    return this.breakdown.reduce(
      (totals, homeroom) => {
        totals.M += homeroom.M;
        totals.F += homeroom.F;
        totals.N += homeroom.N;
        totals.U += homeroom.U;
        totals.total += homeroom.total;

        ['ethnicities', 'moeEthnicities'].forEach((field) => {
          homeroom[field].forEach((ethnicity) => {
            let total = findBy(totals[field], 'code', ethnicity.code);
            if (isEmpty(total)) {
              // This ethnicity hasn't yet been added to the totals - so we can just push the whole counts object in.
              totals[field].push(Object.assign({}, ethnicity));
            } else {
              // This ethnicity has already been added to the totals - so we need to add to the existing set.
              total.count += ethnicity.count;
            }
          });
        });

        ['yearLevels', 'moeYearLevels'].forEach((field) => {
          if (isEmpty(totals[field])) {
            totals[field] = {
              M: 0,
              F: 0,
              N: 0,
              U: 0,
              total: 0,
              yearLevels: [],
            };
          }
          homeroom[field].yearLevels.forEach((yearLevel) => {
            let total = totals[field].yearLevels.find(
              (item) => item.year === yearLevel.year,
            );
            if (isEmpty(total)) {
              // This year level hasn't yet been added to the totals - so we can just push the whole counts object in.
              totals[field].yearLevels.push(Object.assign({}, yearLevel));
            } else {
              // This year level has already been added to the totals - so we need to add to the existing set.
              // Individual year's totals
              total.M += yearLevel.M;
              total.F += yearLevel.F;
              total.N += yearLevel.N;
              total.U += yearLevel.U;
              total.total += yearLevel.total;
            }
            // Gender totals for all years are calculated separately,
            // as schools may have students' data outside the defined year levels
            // Totals for all years per field
            totals[field].M += yearLevel.M;
            totals[field].F += yearLevel.F;
            totals[field].N += yearLevel.N;
            totals[field].U += yearLevel.U;
            totals[field].total += yearLevel.total;
          });
        });

        return totals;
      },
      {
        M: 0,
        F: 0,
        N: 0,
        U: 0,
        total: 0,
        ethnicities: [],
        moeEthnicities: [],
        yearLevels: [],
        moeYearLevels: [],
      },
    );
  }

  @computed('model.learners.[]')
  get ethnicityData() {
    let ethnicities = this.model.learners.map((item) => item.displayEthnicity);
    let ethnicityMap = ethnicities.reduce((data, ethnicity) => {
      data[ethnicity] = data[ethnicity] ? data[ethnicity] + 1 : 1;
      return data;
    }, {});
    return Object.keys(ethnicityMap).reduce((data, key) => {
      data.push({
        value: key,
        label: key,
        count: ethnicityMap[key],
        tooltip: `${key}: ${ethnicityMap[key]}`,
      });
      return data;
    }, []);
  }

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  detailDialogHeading;

  @tracked
  detailDialogOpen;

  @tracked
  filteredLearners;

  @computed('school.{yearLevels.[],schoolType}')
  get yearLevels() {
    let yearLevels = this.school.yearLevels;
    if (parseInt(this.school.schoolType, 10) === 21) {
      // We add years 7 & 8 for Contributing Primary Schools for this report
      yearLevels.push('7', '8');
    }
    return yearLevels;
  }

  @action
  openDetail(type, slice) {
    let learners = this.model.learners;
    let field = 'gender';
    switch (type) {
      case 'moeEthnicity':
        field = 'displayEthnicity';
    }

    if (field === 'gender' && slice.value === '?') {
      // Unknown/other gender - filter out M and F.
      this.filteredLearners = rejectBy(
        rejectBy(learners, field, 'M'),
        field,
        'F',
      );
    } else {
      // Filter for the value from the slice directly.
      this.filteredLearners = filterBy(learners, field, slice.value);
    }

    this.detailDialogHeading = slice.label;
    this.detailDialogOpen = true;
  }
}
